/* THE FORAGER IS A THREE-DIMENSIONAL SKELETON, DRAWN IN TWO (LS-198).
 *
 * Vijay: "not quite i think maybe we should use a 3d model as the base for
 * the 8 directional movement and then project that to 2d sprites" -- and
 * "it needs to fit with the aesthetic tho this is critical".
 *
 * The eight facings of LS-194 were a 2D trick: the front and back views
 * squeezed sideways with the near limbs stepped forward. It reads at a
 * glance, but it is a lie about depth -- the shoulders do not actually turn,
 * a diagonal step is not actually foreshortened, and the arm that swings the
 * sickle sweeps through a circle that stays a circle whichever way the body
 * faces. There is no way to fix that in two dimensions, because the thing
 * that is missing IS the third one.
 *
 * So the figure is now a skeleton in three dimensions -- a stack of joints in
 * character space, with a real walk cycle written as angles rather than as
 * screen offsets -- rotated by the heading and projected through the game's
 * camera. What comes out the other end is the SAME `Pose` the painter has
 * always taken: twelve two-dimensional joints, a view, a flip, a yaw. Not one
 * pixel of `paintPose` changes, so the tunic, the scarf, the hair, the boots,
 * the one-pixel outline and the palette are exactly what they were. The
 * aesthetic is not a thing this pass gets to touch; the only thing that
 * changes is where the joints are, and now they are somewhere true.
 *
 * THE CAMERA. The game is a top-down three-quarter view: height is nearly
 * full on the screen (a standing body is thirteen pixels tall) and depth is
 * squashed to about half, which is what the tiles do too. So a point at
 * character height `y` and ground depth `z` lands at `-y * HK + z * DK`.
 * That single pair of numbers is the whole projection, and it is why a
 * diagonal walk foreshortens on its own rather than by a fudge factor.
 *
 * THE GROUND PLANE is the screen's: X is screen right, Z is screen DOWN, so a
 * heading angle in screen radians (0 east, +PI/2 south, the same angle the
 * sickle swings on) turns straight into a forward vector without a second
 * convention to get wrong.
 */
import type { XY, Pose, RigState } from './rig.ts';
import { SWING, axeMotion } from './swing.ts';
/* The cell is 32x32 (rig.ts RIG_W/RIG_H). Restated rather than imported: the
 * two modules import each other, and a value import here runs before rig.ts
 * has finished defining its constants. The soak checks the two agree. */
const CELL_W = 48;

export type XYZ = [number, number, number];

/* The camera: height full, depth at half. THE PROPORTIONS ARE NOT NEGOTIABLE
 * -- the painter draws a head three pixels across onto a body whose hip,
 * chest, neck and crown it expects at particular heights, and the first pass
 * at this put the crown 1.7 pixels low, which buried the scarf under the head
 * and turned the figure into a sack. Every height below is the height the 2D
 * rig had (floor 23.5, hip 17.5, chest 14, neck 12, crown 9.4), so a body
 * facing the camera projects to the same picture it always did and the turn
 * is the only thing three dimensions add. */
export const HK = 1, DK = 0.5;
const CX = CELL_W / 2, FLOOR = 31.5;   // the feet on the body's bottom row, as rig.ts has it

/* The skeleton, in character space: x is the body's right, y is up from the
 * ground, z is the way it faces. A thirteen-pixel body. */
const HIP_Y = 6.0, CHEST_Y = 9.5, NECK_Y = 11.5, HEAD_Y = 14.1;
const HIP_W = 1.6, SH_W = 2.6;
const HAND_Y = 3.6, HAND_W = 3.4;      // the hands hang beside the hips
const THIGH = 3.3, SHIN = 3.1, UPPER = 3.2, FORE = 3.4;

const add3 = (a: XYZ, b: XYZ): XYZ => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const sub3 = (a: XYZ, b: XYZ): XYZ => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const mul3 = (a: XYZ, k: number): XYZ => [a[0] * k, a[1] * k, a[2] * k];
const len3 = (a: XYZ) => Math.hypot(a[0], a[1], a[2]);
const norm3 = (a: XYZ): XYZ => { const l = len3(a) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };

/** Two-bone IK in three dimensions: the middle joint for a limb from `root`
 *  reaching `target`, bent toward `pole`. The target is pulled in if the limb
 *  cannot reach, which is what a leg at full stride does anyway. */
export function ik3(root: XYZ, target: XYZ, l1: number, l2: number, pole: XYZ): XYZ {
  let d = sub3(target, root);
  let L = len3(d) || 0.001;
  const max = l1 + l2 - 0.05;
  if (L > max) { d = mul3(d, max / L); L = max; }
  const u = mul3(d, 1 / L);
  // the pole, made perpendicular to the bone line
  const dot = pole[0] * u[0] + pole[1] * u[1] + pole[2] * u[2];
  let p = sub3(pole, mul3(u, dot));
  if (len3(p) < 1e-4) p = sub3([0, -1, 0], mul3(u, -u[1]));   // a straight limb bends downward
  p = norm3(p);
  const cosA = Math.max(-1, Math.min(1, (l1 * l1 + L * L - l2 * l2) / (2 * l1 * L)));
  const a = Math.acos(cosA);
  return add3(root, add3(mul3(u, Math.cos(a) * l1), mul3(p, Math.sin(a) * l1)));
}

/** The heading of one of the eight facings, in screen radians. */
export function angleOf(dir: string): number {
  const A: Record<string, number> = {
    right: 0, 'down-right': Math.PI / 4, down: Math.PI / 2, 'down-left': Math.PI * 0.75,
    left: Math.PI, 'up-left': -Math.PI * 0.75, up: -Math.PI / 2, 'up-right': -Math.PI / 4,
  };
  return A[dir] ?? Math.PI / 2;
}

const q = (v: number) => Math.round(v * 2) / 2;

/** Pose the skeleton and project it. The return is the painter's `Pose`. */
export function pose3d(s: RigState): Pose {
  /* THE SPIN TURNS THE BODY (LS-207). Vijay: "fix ... spin animation". The
   * blade went round and the body stood square, which read as an arm on a
   * post. Through the swing's live frames the whole figure faces where the
   * blade is, so it turns through every view in fourteen frames. */
  const spinning = s.attack > SWING.follow && s.attack <= SWING.live && s.swingKind === 'spin';
  const a = spinning ? s.swingAngle : angleOf(s.dir);
  const cosA = Math.cos(a), sinA = Math.sin(a);
  // forward and right on the ground plane (screen X right, screen Z down)
  const F: XYZ = [cosA, 0, sinA], R: XYZ = [-sinA, 0, cosA], UP: XYZ = [0, 1, 0];
  /* THE ONE STYLISATION (LS-198). A true camera puts the body's LEFT-RIGHT
   * axis into depth when the figure faces east or west, so the far shoulder
   * would ride a pixel and a half above the near one and the two boots would
   * stand on different rows. That is geometrically right and it is not what a
   * side view looks like in this kind of art: the classic side view keeps the
   * feet on one line and reads the far limb as darker, not as higher. So the
   * body's lateral axis only takes 45% of its depth, while everything the
   * animation moves along the FORWARD axis -- the stride, the arm swing, the
   * blade's sweep -- takes the full projection and foreshortens honestly.
   * One number, in one place, and the rest of the module is the geometry. */
  const LAT = 0.45;
  const world = (x: number, y: number, z: number): XYZ =>
    [R[0] * x + F[0] * z, y, R[2] * x * LAT + F[2] * z];
  /** world -> the screen, through the camera */
  const flat = (p: XYZ): XY => [CX + p[0], FLOOR - p[1] * HK + p[2] * DK];

  const t = s.anim;
  const inSwing = s.attack > 0;
  const axe = axeMotion(s.swingKind, s.attack);
  // the legs keep walking through a swing (LS-207): a strike rides the stride
  const walking = s.moving && s.dodge <= 0 && !s.reel;
  // THE CHARGE (LS-207): the button held toward a spin. The body sinks and
  // winds up, the blade drawn back over the shoulder, and it trembles once
  // the spin is ready.
  const charging = !inSwing && (s.charge || 0) > 0 && s.dodge <= 0 && !s.reel;
  const ragdolling = (s.ragdoll || 0) > 0 && s.dodge <= 0;
  const ragR = ragdolling ? 1 - (s.ragdoll || 0) / (s.ragdollMax || 26) : 0;
  const flinching = !ragdolling && (s.flinch || 0) > 0 && s.dodge <= 0;
  const recoilK = (s.recoil || 0) > 0 && s.dodge <= 0 ? (s.recoil || 0) / (s.recoilMax || 8) : 0;   // the blade kicked back off stone (LS-213)
  const flK = flinching ? Math.sin((s.flinch || 0) / (s.flinchMax || 14) * Math.PI * 0.5) : 0;   // full at the blow, easing out
  const flD: XYZ = flinching ? [(s.ragdollDir || [1, 0])[0], 0, (s.ragdollDir || [1, 0])[1]] : [0, 0, 0];
  const chargeK = charging ? Math.min(1, (s.charge || 0) / 28) : 0;
  // a small, slow tremble (LS-210, "the sword is shaking a lot during spin charge"): a twentieth of a radian, not a fifth
  const tremble = charging && (s.charge || 0) >= 28 ? Math.sin(s.anim * 0.9) * 0.05 : 0;
  const ph = walking ? (s.stride ?? t * 0.30) : 0;
  const u = inSwing ? Math.min(1, Math.max(0, (SWING.live - s.attack) / (SWING.live - SWING.follow))) : 0;

  /* ---- the trunk. A walk bobs and counter-rotates: the hips turn with the
   * leading leg and the shoulders turn against them, which is the thing that
   * makes a walk cycle read as walking rather than as a puppet sliding. */
  const bob = walking ? Math.abs(Math.sin(ph)) * 0.5 : 0;
  const breathe = walking || inSwing ? 0 : Math.sin(t / 22) * 0.9;   // a whole pixel at the top of the breath: 0.15 px at the chest rounded to nothing and the forager stood like a statue (the art lens, round 39)
  const hipTurn = walking ? Math.sin(ph) * 0.20 : 0;
  let shTurn = walking ? -Math.sin(ph) * 0.26 : 0;
  let lean: XYZ = [0, 0, 0], crouch = 0;

  if (s.dodge > 0) {
    /* THE ROLL IS A ROLL (LS-210). Vijay: "roll animation is bad and needs
     * to be improved". It was a crouch that slid: the body sank into a ball
     * and leaned along the dash, and nothing turned over. Now the tucked
     * body is turned head over heels about the hips, a full turn along the
     * dash over the roll's frames (eased, so it starts and lands on its
     * feet), lifting a little off the ground through the middle -- see the
     * somersault below, applied to every joint before projection. */
    const r = 1 - s.dodge / (s.dodgeMax || 9);
    const tuck = Math.sin(Math.min(1, r * 1.15) * Math.PI);
    crouch = tuck * 4;
    lean = [s.dodgeDir[0] * 1.6, 0, s.dodgeDir[1] * 1.6 / DK * 0.55];
  } else if (ragdolling) {
    // flung: the trunk is turned over below; here only a little crouch as it lands and gets up
    crouch = ragR > 0.75 ? (1 - ragR) * 4 * 1.2 : 0.4;
  } else if (flinching) {
    // the small hit: bent away from the blow, sunk a little
    lean = [flD[0] * 2.4 * flK, 0, flD[2] * 2.4 * flK / DK * 0.55];
    crouch = 0.8 * flK;
  } else if (s.reel) {
    lean = mul3(F, -1.4);
  } else if (inSwing) {
    // the swing leans along the blade's own direction, in the ground plane
    const sa = s.swingAngle, sc = Math.cos(sa), ss = Math.sin(sa);
    const push = s.attack > SWING.live ? -(s.attack - SWING.live) / (SWING.total - SWING.live) * 1.1
      : s.attack > SWING.follow ? (s.swingKind === 'thrust' ? 1.9 : s.swingKind === 'spin' ? 0 : 1.1)
        : s.attack / SWING.follow * (s.swingKind === 'thrust' ? 1.4 : 0.9);   // the follow-through: the lean eases out
    lean = [sc * push, 0, ss * push];
    if (recoilK > 0) { const rd = s.recoilDir || [1, 0]; lean = [rd[0] * 1.8 * recoilK, 0, rd[1] * 1.8 * recoilK / DK * 0.55]; crouch = 0.6 * recoilK; }   // jolted back off what it struck
    if (s.swingKind === 'thrust' && s.attack <= SWING.live) crouch = 0.9 * Math.min(1, s.attack / 6 + 0.4);
    if (axe) crouch = axe.crouch;
    if (spinning) crouch = 1.1;
    shTurn = s.swingKind === 'fore' ? 0.5 - u : s.swingKind === 'back' ? -0.5 + u : shTurn;
  } else if (charging) {
    crouch = 0.6 + chargeK * 1.2;
    shTurn = 0.55 * chargeK;
    lean = mul3(F, -0.6 * chargeK);
  }

  const hipC: XYZ = add3(world(0, HIP_Y - bob + crouch * 0.35, 0), mul3(lean, 0.3));
  const chest: XYZ = add3(world(0, CHEST_Y - bob * 0.8 - breathe - crouch * 0.9, 0.2), mul3(lean, 0.75));
  const neck: XYZ = add3(world(0, NECK_Y - bob * 0.7 - crouch * 1.3, 0.15), mul3(lean, 0.85));
  // the head leads a little into the walk and turns with the glance
  const glanceT = ((t % 260) + 260) % 260;
  const glance = (!walking && !inSwing && s.dodge <= 0 && glanceT > 200 && glanceT < 230)
    ? (glanceT < 206 ? (glanceT - 200) / 6 : glanceT > 224 ? (230 - glanceT) / 6 : 1) : 0;
  const head: XYZ = add3(add3(world(glance * 1.2, HEAD_Y - bob * 0.6 - crouch * 1.8, 0.1 + (walking ? Math.cos(ph - 0.6) * 0.2 : 0)), mul3(lean, 0.9)),
    [0, 0, 0]);

  /* ---- the legs. A foot target travels forward and back along the body's
   * own forward axis and lifts on the way through, so a diagonal stride is
   * foreshortened by the projection and by nothing else. */
  const hips: XYZ[] = [];
  const feet: XYZ[] = [];
  const knees: XYZ[] = [];
  for (const i of [0, 1]) {
    const sgn = i === 0 ? 1 : -1;                     // 0 = the body's right
    const phase = ph + (i === 0 ? 0 : Math.PI);
    /* THE FOOT IN THE AIR SWINGS FORWARD (LS-216a). Vijay: "the player's walk
     * animation is backwards". The lift is on for half the cycle (sine
     * positive) and the swing ran cos over the same half, which carries the
     * lifted foot from front to back: a moonwalk. The airborne foot goes
     * back to front, and the planted one front to back under the body. */
    const swing = walking ? -Math.cos(phase) * 2.9 : 0;
    const lift = walking ? Math.max(0, Math.sin(phase)) * 1.8 : 0;
    const hx = sgn * HIP_W, hz = hipTurn * sgn * 1.2;
    const hip = add3(world(hx, HIP_Y - bob + crouch * 0.35, hz), mul3(lean, 0.3));
    let footZ = swing, footY = lift, footX = sgn * (HIP_W + 0.1);
    if (!walking) {
      if (s.dodge > 0) { footZ = sgn * 0.6 - 1.6; footY = crouch * 0.9; }
      else if (ragdolling) { footZ = sgn * 0.9 + 1.4; footY = ragR < 0.75 ? 1.2 : 0.2; footX = sgn * (HIP_W + 0.6); }
      else if (flinching) { footZ = sgn * 0.5 - 0.8 * flK; footX = sgn * (HIP_W + 0.4 * flK); }
      else if (s.reel) { footZ = -1.6; footX = sgn * 2.4; }
      else if (inSwing && s.swingKind === 'thrust' && s.attack <= SWING.live) footZ = sgn > 0 ? 2.6 : -1.6;
      else if (inSwing) footZ = sgn * 0.4;
      else footZ = sgn * 0.3;
    }
    const foot = add3(world(footX, footY, footZ), mul3(lean, 0.1));
    // the knee bends forward, and away from the body a touch so the legs read
    // as two legs when the figure is seen head on
    const pole = norm3(add3(mul3(F, 1), mul3(R, sgn * 0.12)));
    hips.push(hip); feet.push(foot);
    knees.push(ik3(hip, foot, THIGH, SHIN, pole));
  }

  /* ---- the arms. They counter-swing with the legs; the swinging arm reaches
   * along the blade's direction, in the ground plane, at chest height. */
  const shoulders: XYZ[] = [];
  const hands: XYZ[] = []; const shs: XYZ[] = [];
  const elbows: XYZ[] = [];
  let swingSide = 0;   // 0 = the body's right arm carries the sickle
  for (const i of [0, 1]) {
    const sgn = i === 0 ? 1 : -1;
    const sh = add3(world(sgn * SH_W, CHEST_Y - bob * 0.8 - crouch * 0.9, 0.1 + shTurn * sgn * 1.2), mul3(lean, 0.75));
    shs.push(sh);
    shoulders.push(sh);
    let hand: XYZ;
    if (inSwing && i === swingSide) {
      const sa = s.swingAngle;
      let ang = sa, reach = 5.6;
      if (recoilK > 0) {                              // the blade kicked back off stone or metal (LS-213): the arm snaps in and back
        const kick = (s.swingKind === 'back' ? 1 : s.swingKind === 'thrust' ? 0 : -1) * 0.8 * recoilK;
        ang = sa + kick; reach = 4.0 + (1 - recoilK) * 2.4;
      } else if (s.attack > SWING.live) {                    // the wind-up, pulled back
        const w = (s.attack - SWING.live) / (SWING.total - SWING.live);
        const back = s.swingKind === 'thrust' ? Math.PI : Math.PI * 0.75 * (s.swingKind === 'back' ? -1 : 1);
        ang = sa + back * w; reach = 4.2 + (1 - w) * 2.6;
      } else if (s.attack > SWING.follow) {
        reach = s.swingKind === 'thrust' ? 9.2 : 7.4;
      } else {                                        // the follow-through: the arm carries on, then comes home
        const w = s.attack / SWING.follow;
        reach = 3.6 + 3.8 * Math.min(1, w * 1.4); ang = sa;   // the overshoot is in the angle itself (swingAngle)
      }
      /* THE SWEEP IS A CIRCLE ON THE GROUND, AT SHOULDER HEIGHT. Dropping
       * the hand below the shoulder here put a fixed downward offset into
       * every stroke, so the blade no longer pointed along the swing once the
       * depth half was un-projected: the arm's screen angle drifted by up to
       * a fifth of a radian and thirty-six of the rig's own poses said so. */
      if (axe && recoilK <= 0) { ang = sa; reach = axe.reach; }
      hand = [sh[0] + Math.cos(ang) * reach, sh[1] - 0.2 + (recoilK <= 0 ? axe?.lift || 0 : 0), sh[2] + Math.sin(ang) * reach];
    } else if (charging && i === swingSide) {
      // drawn back over the shoulder, higher the longer it is held
      const ang = a + Math.PI * 0.82 + tremble, reach = 4.2 + chargeK * 1.6;
      hand = [sh[0] + Math.cos(ang) * reach, sh[1] + 0.8 + chargeK * 1.8, sh[2] + Math.sin(ang) * reach];
    } else if (s.dodge > 0) {
      hand = add3(world(sgn * 1.8, HAND_Y + 1.6 - crouch * 0.4, 1.4), mul3(lean, 0.4));
    } else if (ragdolling) {
      // arms flung up and out, loose
      hand = world(sgn * 3.4, HAND_Y + (ragR < 0.75 ? 4.2 : 1.0), -1.2 + sgn * 0.4);
    } else if (flinching) {
      // arms thrown up and back with the blow
      hand = add3(world(sgn * (HAND_W + 1.2 * flK), HAND_Y + 3.6 * flK, -0.6 * flK), mul3(lean, 0.6));
    } else if (s.reel) {
      hand = add3(world(sgn * 3.6, CHEST_Y - 0.4, -1.4), [0, 0, 0]);
    } else {
      const armSwing = walking ? Math.cos(ph + (i === 0 ? Math.PI : 0)) * 2.2 : 0;
      hand = add3(world(sgn * HAND_W, HAND_Y + breathe * 0.6 - crouch * 0.5, armSwing), mul3(lean, 0.4));
    }
    hands.push(hand);
    // the elbow bends back and out: away from the chest, behind the hand
    // the elbow sits a little outside and behind the line of the arm, which
    // is where an elbow is; poling it sideways alone made wings
    const pole = norm3([R[0] * sgn * 0.55 - F[0] * 0.35, -0.75, R[2] * sgn * 0.55 - F[2] * 0.35]);
    elbows.push(ik3(sh, hand, UPPER, FORE, pole));
  }

  /* ---- the projection, and the depth order. The painter draws one leg and
   * one arm behind the trunk and the others in front, so the arrays are
   * ordered by depth: for the legs index 0 is the near one, for the arms
   * index 1 is (the painter's orders are historical, and this is where the
   * two conventions are reconciled). */
  if (s.dodge > 0 || ragdolling) {
    // the somersault (LS-210), or the fall (LS-212): every joint turned about
    // the hips in the plane of the direction of travel and up
    let th = 0, hop = 0, dir: XY = s.dodgeDir;
    if (s.dodge > 0) {
      const r = 1 - s.dodge / (s.dodgeMax || 9);
      const ease = r * r * (3 - 2 * r);
      th = ease * Math.PI * 2;
      hop = Math.sin(r * Math.PI) * 2.0;
    } else {
      /* THE FALL: flung back with the head trailing, on its back by a third
       * of the way, lying until three quarters, then up again. The hips
       * come down to the ground while it lies. */
      dir = s.ragdollDir || [1, 0];
      const fall = Math.min(1, ragR / 0.35), up = ragR > 0.75 ? (ragR - 0.75) / 0.25 : 0;
      const phi = 1.35 * (fall * fall * (3 - 2 * fall)) * (1 - up * up * (3 - 2 * up));
      th = phi;   // positive: the head goes along the travel, so the body lands on its back with the feet toward the blow (Vijay: "i should fall back not forward")
      const lying = phi / 1.35;
      hop = -(HIP_Y - 1.6) * lying + (ragR < 0.35 ? Math.sin(ragR / 0.35 * Math.PI) * 1.5 : 0);
    }
    const dl = Math.hypot(dir[0], dir[1]) || 1;
    const d: XYZ = [dir[0] / dl, 0, dir[1] / dl];
    const c = Math.cos(th), sn = Math.sin(th);
    const roll = (p: XYZ): XYZ => {
      const v: XYZ = [p[0] - hipC[0], p[1] - hipC[1], p[2] - hipC[2]];
      const a = v[0] * d[0] + v[2] * d[2], b = v[1];
      const rest: XYZ = [v[0] - d[0] * a, 0, v[2] - d[2] * a];
      const a2 = a * c + b * sn, b2 = -a * sn + b * c;
      return [hipC[0] + rest[0] + d[0] * a2, Math.max(0.2, hipC[1] + b2 + hop), hipC[2] + rest[2] + d[2] * a2];
    };
    const chestR = roll(chest), neckR = roll(neck), headR = roll(head);
    chest[0] = chestR[0]; chest[1] = chestR[1]; chest[2] = chestR[2];
    neck[0] = neckR[0]; neck[1] = neckR[1]; neck[2] = neckR[2];
    head[0] = headR[0]; head[1] = headR[1]; head[2] = headR[2];
    for (const arr of [shoulders, elbows, hands, knees, feet]) for (let i = 0; i < arr.length; i++) { const q = roll(arr[i]); arr[i] = q; }
    hipC[1] = Math.max(0.2, hipC[1] + hop);
  }
  const nearLeg = feet[0][2] >= feet[1][2] ? 0 : 1;
  const nearArm = hands[0][2] >= hands[1][2] ? 0 : 1;
  const legOrder = [nearLeg, 1 - nearLeg];
  const armOrder = [1 - nearArm, nearArm];

  const hip2 = flat(hipC), chest2 = flat(chest), neck2 = flat(neck), head2 = flat(head);
  const kn: [XY, XY] = [flat(knees[legOrder[0]]), flat(knees[legOrder[1]])];
  const ft: [XY, XY] = [flat(feet[legOrder[0]]), flat(feet[legOrder[1]])];
  const sh2: [XY, XY] = [flat(shoulders[armOrder[0]]), flat(shoulders[armOrder[1]])];
  const el: [XY, XY] = [flat(elbows[armOrder[0]]), flat(elbows[armOrder[1]])];
  const ha: [XY, XY] = [flat(hands[armOrder[0]]), flat(hands[armOrder[1]])];
  const swingArm: 0 | 1 = (armOrder[1] === swingSide ? 1 : 0);

  /* ---- what the head shows. The painter draws the face from `view`, `flip`
   * and `yaw`; they are read off the heading rather than chosen by a table,
   * so they stay right if the camera ever tilts. */
  const view: Pose['view'] = sinA > 0.4 ? 'front' : sinA < -0.4 ? 'back' : 'side';
  const flip = view === 'side' ? cosA < 0 : false;
  const yaw = view === 'side' ? 0 : Math.abs(cosA) < 0.2 ? 0 : (cosA > 0 ? 1 : -1);

  /* ---- the blade. It sweeps in the ground plane, so its screen angle is the
   * swing angle SEEN BY THE CAMERA: the depth half of it foreshortens, which
   * is why an overhead cut to the north is a short stroke and the same cut to
   * the east is a long one. */
  let sickle: Pose['sickle'] = null;
  /* THE SAME SHAPE `rig.ts` ALREADY DECLARES for this list: a smear segment carries a width and a dim flag as
   * well, and both are pushed a few lines down. This copy of the type named three of the five (2026-09-06). */
  const trail: { at: XY; ang: number; len: number; w?: number; dim?: boolean }[] = [];
  if (inSwing) {
    const sa = s.swingAngle;
    const flatAng = Math.atan2(Math.sin(sa) * DK, Math.cos(sa));
    const foreshorten = Math.hypot(Math.cos(sa), Math.sin(sa) * DK);
    // the last stab is the big one (LS-207): the thrust's blade reaches further than a sweep's
    const len = (s.swingKind === 'thrust' ? 12 : 11) * (0.8 + 0.2 * foreshorten);
    /* THE TORCH'S SWEEP TAKES ITS HEIGHT FROM `axeMotion` AND ITS SMEAR FROM
     * THE ARC (2026-09-10, LS-336). This read "does axeMotion have anything to
     * say", which was the same question as "is this a chop or a heave" until
     * the sweep was given a height profile too -- and then the sweep fell into
     * the axe's three-stroke smear, whose strokes lag by the heave's angle or
     * not at all, so all three landed exactly on the blade. Measured on the
     * pose sheet and in test/_probe_trail.ts: three segments, all at the
     * blade's own angle, which is no smear at all. It wants the ten-spoke fan,
     * which is what the comment below has always called a sweep. */
    const sweep = (axe && s.swingKind !== 'sweep') || s.swingKind === 'thrust' ? 0 : s.swingKind === 'back' ? -1 : 1;
    const lag = s.attack > SWING.follow && s.attack <= SWING.live ? 0.18 * sweep : 0;
    const kickBack = recoilK > 0 ? -(sweep || 0) * 0.9 * recoilK + (s.swingKind === 'thrust' ? 0.5 * recoilK : 0) : 0;   // LS-213
    sickle = { at: ha[swingArm], ang: flatAng - lag + kickBack, len: recoilK > 0 ? len * (1 - 0.15 * recoilK) : len };
    /* THE SMEARS (LS-207). A sweep leaves three segments behind the blade,
     * each one further back along the arc, thinner and darker -- and only
     * where the blade has already been, so the smear grows with the swing.
     * A spin leaves four, so the ring reads as a ring. A thrust leaves a
     * streak along its line behind the point. */
    const sh0 = shoulders[swingSide];
    const late = s.attack <= SWING.follow;   // into the follow-through the smear is all shadow
    if (sweep && s.attack > SWING.follow - 4 && s.attack <= SWING.live) {
      const prog = u;
      // close enough together that the spokes fill into a fan, not a set of claws
      // EXAGGERATED (Vijay: "exaggerate the smears more"): the fan reaches
      // back most of the arc, ten spokes for a sweep and fourteen for a
      // spin, the nearest three bright and wide, the far ones long shadows
      const steps = s.swingKind === 'spin'
        ? [0.14, 0.28, 0.42, 0.56, 0.7, 0.84, 0.98, 1.12, 1.26, 1.4, 1.54, 1.68, 1.82, 1.96]
        : [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0];
      const n = steps.length;
      steps.forEach((off, k) => {
        if (s.swingKind !== 'spin' && off > prog * 2.3 + 0.15) return;
        const pa = sa - sweep * off;
        const back: XYZ = [sh0[0] + Math.cos(pa) * 7.5, sh0[1] - 0.2, sh0[2] + Math.sin(pa) * 7.5];
        trail.push({ at: flat(back), ang: Math.atan2(Math.sin(pa) * DK, Math.cos(pa)), len: 12 - k * (4 / n), w: 2.6 - k * (1.6 / n), dim: late || k > 2 });
      });
    } else if (axe && s.attack > SWING.follow - 2 && s.attack < SWING.live && recoilK <= 0) {
      for (const k of [1, 2, 3]) {
        if (s.attack + k > SWING.live) continue;
        const previous = axeMotion(s.swingKind, s.attack + k)!;
        const previousU = Math.max(0, Math.min(1, (SWING.live - s.attack - k) / (SWING.live - SWING.follow)));
        const pa = sa - (s.swingKind === 'heave' ? (u - previousU) * 0.9 : 0);
        const at: XYZ = [sh0[0] + Math.cos(pa) * previous.reach, sh0[1] - 0.2 + previous.lift, sh0[2] + Math.sin(pa) * previous.reach];
        trail.push({ at: flat(at), ang: Math.atan2(Math.sin(pa) * DK, Math.cos(pa)), len: 7 - k * 0.5, w: 2.1 - k * 0.3, dim: late || k > 1 });
      }
    } else if (s.swingKind === 'thrust' && s.attack > SWING.follow - 2 && s.attack <= SWING.live) {
      // the stab's streak: four segments back along the line, the near ones broad
      for (const k of [0, 1, 2, 3]) {
        const r = 9.4 - k * 2.2;
        const p: XYZ = [sh0[0] + Math.cos(sa) * r, sh0[1] - 0.2, sh0[2] + Math.sin(sa) * r];
        trail.push({ at: flat(p), ang: flatAng, len: 5.2 - k * 0.5, w: 2.4 - k * 0.45, dim: late || k > 1 });
      }
    }
  } else if (charging) {
    // the blade cocked over the shoulder, its point up and back
    const ang = a + Math.PI * 0.82 + tremble;
    sickle = { at: ha[swingArm], ang: Math.atan2(Math.sin(ang) * DK, Math.cos(ang)) - 0.9, len: 10 };
  }

  /* THE SKELETON ITSELF, for a probe or the 3D view (LS-398, 2026-09-13): the joints before they are flattened, in the
   * body's own frame (x across, y up, z toward the camera), so the body can stand as capsules in a scene. Inert otherwise. */
  { const cap3 = (globalThis as any).__m3Capture; if (cap3) cap3.push({ kind: 'rig', hip: hipC, chest, neck, head, hips, knees, feet, shoulders: shs, elbows, hands, view, flip, dir: s.dir }); }
  const key = ['3d', view, flip ? 1 : 0, yaw, s.lit ? 1 : 0, s.attack, s.swingKind, charging ? Math.round(chargeK * 8) + (tremble ? 't' + Math.round(tremble * 10) : '') : 0,
    q(hip2[0]), q(hip2[1]), q(chest2[0]), q(chest2[1]), q(head2[0]), q(head2[1]),
    ...ft.flat().map(q), ...kn.flat().map(q), ...el.flat().map(q), ...ha.flat().map(q),
    sickle ? Math.round(sickle.ang * 16) + ':' + Math.round(sickle.len * 2) : '-', trail.map((t) => Math.round(t.ang * 8) + ':' + Math.round(t.len)).join('/')].join(',');

  return { view, flip, yaw, hip: hip2, chest: chest2, neck: neck2, head: head2, sh: sh2, el, ha, kn, ft, sickle, trail, swingArm, lit: s.lit, key };
}
